import { IAllComments } from "@/interfaces/entities/comments";
import { tableBackgroundColor } from "../utils/tableBackgroundColor";
import {
    IoIosCheckmarkCircle,
    IoIosCloseCircle,
    IoIosHelpCircle,
} from 'react-icons/io';
import { eventStatusColor } from "../utils/eventStatusColor";
import { EventStatus } from "@/interfaces/entities/event";
import Pagination from "../Pagination";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { formatDateForDisplay } from "../utils/timeFormatter";
import { useGetBlogComments } from "@/hooks/comments/useGetBlogComments";
import useChangeCommentStatus from "@/hooks/comments/useChangeCommentStatus";
import Loader from "../Loader";
import Error from "../Error";
import { CommentsError } from "@/exceptions/comments/comments.enum";


export const CommentsTable = () => {
    const params = useParams<{ type: string; id: string }>();

    // Now you can get type and id from params:
    const { type, id } = params;
    const [currentPage, setCurrentPage] = useState(1);

    const { comments, isLoading, isError, error, totalPages, isPending } =
        useGetBlogComments(currentPage, Number(id));
    const { mutate } = useChangeCommentStatus();
    const isFetching = isLoading || isPending;

    if (isFetching) {
        return <Loader />;
    }

    if (isError) {
        return (
            <Error message={error ? error.message : CommentsError.COMMENT_ERROR} />

        );
    }


    const eventStatus = (status: EventStatus) => {
        const color = eventStatusColor(status);
        switch (status) {
            case EventStatus.APPROVED:
                return (
                    <IoIosCheckmarkCircle
                        className={`${color} inline-block align-middle`}
                        title={EventStatus.APPROVED}
                        size={22}
                    />
                );
            case EventStatus.REJECTED:
                return (
                    <IoIosCloseCircle
                        className={`${color} inline-block align-middle`}
                        title={EventStatus.REJECTED}
                        size={22}
                    />
                );
            case EventStatus.PENDING:
                return (
                    <IoIosHelpCircle
                        className={`${color} inline-block align-middle`}
                        title={EventStatus.PENDING}
                        size={22}
                    />
                );
            default:
                return null;
        }
    };
    return (
        <div className="px-4 sm:px-6 lg:px-8 w-full mb-10">
            <div className="sm:flex sm:items-center">
                <div className="flex justify-between font-bold sm:flex-auto">
                    <h1
                        data-test="h1-categories"
                        className="text-base font-semibold leading-6 text-gray-900"
                    >
                        Comments
                    </h1>
                </div>
            </div>
            {comments.length ? <div className="mt-8 w-full  flow-root">
					<div className=" w-full overflow-x-auto">
						<div className="inline-block  min-w-[1100px] w-full py-2 align-middle">
                        <table className="min-w-full table-auto divide-y divide-gray-300 border-red-300  border-spacing-x-4">
                            <thead>
                                <tr className='gap-x-2'>
                                    <th
                                        scope="col"
                                        className="w-1/12  py-3.5 text-center text-sm font-semibold text-gray-900 "
                                        data-test="th-id"
                                    >
                                        ID
                                    </th>
                                    <th
                                        scope="col"
                                        className="w-1/12 py-3.5 text-center text-sm font-semibold text-gray-900"
                                        data-test="th-name"
                                    >
                                        Name
                                    </th>
                                    <th
                                        scope="col"
                                        className="w-2/12 py-3.5 text-center text-sm font-semibold text-gray-900"
                                        data-test="th-email"
                                    >
                                        Email
                                    </th>
                                    <th
                                        scope="col"
                                        className="w-3/12 py-3.5 text-center text-sm font-semibold text-gray-900"
                                        data-test="th-comment"
                                    >
                                        Comment
                                    </th>
                                    <th
                                        scope="col"
                                        className="w-2/12 py-3.5 text-center text-sm font-semibold text-gray-900"
                                        data-test="th-date"
                                    >
                                        Date & Time
                                    </th>
                                    <th
                                        scope="col"
                                        className=" w-1/12 px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
                                        data-test="th-type"
                                    >
                                        {type === "event" ? "Event ID" : "Blog Id"}
                                    </th>
                                    {/* <th
                                        scope="col"
                                        className="w-1.5/12 px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
                                        data-test="th-Title"
                                    >
                                        Title
                                    </th> */}

                                    <th
                                        scope="col"
                                        className=" w-1/12 px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
                                        data-test="th-status"
                                    >
                                        Status
                                    </th>
                                    <th
                                        scope="col"
                                        className="w-1/12 px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
                                    >
                                        <span className="">Action</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {comments.map((comment: IAllComments, index: number) => {
                                    return (
                                        <tr
                                            key={comment.id}
                                            className={`${tableBackgroundColor(
                                                index,
                                            )} hover:bg-gray-200 min-h-8 gap-x-2`}
                                        >
                                            <td
                                                data-test="td-id"
                                                className="text-center max-w-sm py-4  text-sm font-medium text-gray-900 "
                                            >
                                                {comment.id}
                                            </td>
                                            <td
                                                data-test="td-name"
                                                className="text-center max-w-md py-4 text-sm text-gray-500"
                                            >
                                                {comment.name}
                                            </td>
                                            <td
                                                data-test="td-description"
                                                className="text-center max-w-md py-4 text-sm text-gray-500"
                                            >
                                                {comment.email}
                                            </td>
                                            <td
                                                data-test="td-description"
                                                className="text-center max-w-md py-4 text-sm text-gray-500"
                                            >
                                                {comment.comment.length > 150
                                                    ? `${comment.comment.slice(0, 150)}...`
                                                    : comment.comment}
                                            </td>
                                            <td
                                                data-test="td-date"
                                                className="text-center max-w-md py-4 text-sm text-gray-500"
                                            >
                                                {comment.createdAt && formatDateForDisplay(new Date(comment.createdAt))}
                                            </td>
                                            <td
                                                data-test="td-type"
                                                className="text-center max-w-md py-4 text-sm text-gray-500"
                                            >
                                                {id}
                                            </td>
                                            {/* <td
                                                data-test="td-title"
                                                className="text-center max-w-md py-4 text-sm text-gray-500"
                                            >
                                                {type === 'event'
                                                    ? (comment as IBlogComments)?.blog?.title
                                                    : (comment as IBlogComments)?.blog?.title}
                                            </td> */}

                                            <td
                                                data-test="td-status"
                                                className="px-3 py-3.5 text-center text-sm text-gray-900 break-words"
                                            >
                                                {eventStatus(comment.status)}
                                            </td>
                                            <td className="px-3 py-3.5 text-center text-sm text-gray-900">
                                                {comment.status !== EventStatus.APPROVED && (
                                                    <button
                                                        onClick={() =>
                                                            mutate({
                                                                commentId: comment.id as number,
                                                                status: EventStatus.APPROVED,
                                                            })
                                                        }
                                                        className="text-indigo-600 hover:text-indigo-900 disabled:text-gray-400"
                                                        data-test="btn-approve"
                                                        disabled={isFetching}
                                                    >
                                                        Approve
                                                    </button>
                                                )}
                                                {comment.status !== EventStatus.REJECTED && (
                                                    <button
                                                        onClick={() =>
                                                            mutate({
                                                                commentId: comment?.id as number,
                                                                status: EventStatus.REJECTED,
                                                            })
                                                        }
                                                        className="text-indigo-600 hover:text-indigo-900 disabled:text-gray-400"
                                                        disabled={isFetching}
                                                        data-test="btn-reject"
                                                    >
                                                        Reject
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
                <Pagination
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    totalPages={totalPages as number}
                />
            </div> : <div className="w-full"><h1
                data-test="h1-comments"
                className="text-base font-semibold text-center mt-20 leading-6 text-gray-900"
            >
                No Comments Available
            </h1></div>}

        </div>
    )
}